import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";

interface Job {
  id: string; location_name: string; location_address: string; client_name: string;
  assigned_user_name: string | null; scheduled_at: string; completed_at: string | null;
  status: string; notes: string | null;
}
interface Location { id: string; name: string; address: string; client_name: string; }

export function Schedule() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ locationId: "", scheduledAt: "", notes: "" });

  const loadJobs = async () => { setJobs(await api("/jobs")); };
  const loadLocations = async () => { setLocations(await api("/locations")); };

  useEffect(() => { loadJobs(); loadLocations(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await api("/jobs", { method: "POST", body: JSON.stringify(form) });
    setShowForm(false);
    setForm({ locationId: "", scheduledAt: "", notes: "" });
    loadJobs();
  };

  const handleStatusChange = async (jobId: string, status: string) => {
    const update: any = { status };
    if (status === "completed") update.completedAt = new Date().toISOString();
    await api(`/jobs/${jobId}`, { method: "PUT", body: JSON.stringify(update) });
    loadJobs();
  };

  const statuses = ["scheduled", "in_progress", "completed", "cancelled"];

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
            {t("jobs.title")}
          </h1>
          <p className="text-sm text-navy-400 mt-1">{t("jobs.jobCount", { count: jobs.length })}</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          {t("jobs.addJob")}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-navy-100 p-6 mb-6 animate-slide-in">
          <h3 className="font-semibold mb-4" style={{ fontFamily: "var(--font-display)" }}>{t("jobs.newJob")}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="input-label">{t("jobs.location")} *</label>
              <select value={form.locationId} onChange={(e) => setForm({ ...form, locationId: e.target.value })} required className="input">
                <option value="">{t("jobs.selectLocation")}</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.client_name} — {loc.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="input-label">{t("jobs.scheduledAt")} *</label>
              <input type="datetime-local" value={form.scheduledAt} onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })} required className="input" />
            </div>
          </div>
          <div className="mb-4">
            <label className="input-label">{t("inspections.notes")}</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="input" style={{ resize: "vertical" }} />
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn btn-primary">{t("common.save")}</button>
            <button type="button" onClick={() => setShowForm(false)} className="btn btn-ghost">{t("common.cancel")}</button>
          </div>
        </form>
      )}

      {jobs.length === 0 ? (
        <div className="bg-white rounded-xl border border-navy-100 p-16 text-center">
          <div className="w-14 h-14 bg-navy-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-navy-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-navy-600 font-medium mb-1">{t("jobs.noJobsTitle")}</p>
          <p className="text-navy-400 text-sm">{t("jobs.noJobs")}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
          {jobs.map((job, i) => (
            <div key={job.id} className={`px-5 py-4 flex items-center justify-between gap-4 hover:bg-navy-50/50 transition-colors ${i < jobs.length - 1 ? "border-b border-navy-50" : ""}`}>
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-10 h-10 bg-navy-50 rounded-lg flex items-center justify-center text-navy-400 shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-navy-900">{job.location_name}</p>
                  <p className="text-xs text-navy-400 truncate">{job.client_name} · {job.location_address}</p>
                  {job.notes && <p className="text-xs text-navy-300 mt-0.5 truncate">{job.notes}</p>}
                </div>
              </div>
              <div className="text-right shrink-0 mr-3">
                <p className="text-sm font-medium text-navy-700">
                  {new Date(job.scheduled_at).toLocaleDateString()}
                </p>
                <p className="text-xs text-navy-400">
                  {new Date(job.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
              <select
                value={job.status}
                onChange={(e) => handleStatusChange(job.id, e.target.value)}
                className={`status-select cursor-pointer appearance-none rounded-full px-3 py-1 text-xs font-medium border-0 pr-7 bg-no-repeat bg-[right_6px_center] bg-[length:12px] ${
                  job.status === "scheduled" ? "bg-sky-50 text-sky-700" :
                  job.status === "in_progress" ? "bg-amber-50 text-amber-600" :
                  job.status === "completed" ? "bg-emerald-50 text-emerald-600" : "bg-navy-50 text-navy-500"
                }`}
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%2364748b'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z'/%3E%3C/svg%3E")` }}
              >
                {statuses.map((s) => (
                  <option key={s} value={s}>{t(`jobs.status.${s}`)}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
